/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * API Client - Axios Instance with Interceptors
 * ============================================================================
 *
 * Centralized HTTP client for all API communications.
 * Features: Auth token injection, token refresh, error handling.
 *
 * ============================================================================
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { projectConfig } from '@/config/project.config';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useSupportSessionStore } from '@/stores/supportSessionStore';
import { ApiError, ApiErrorResponse } from './types';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: projectConfig.api.baseUrl,
  timeout: projectConfig.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;

// Track active network error notification to avoid duplicates
let networkErrorNotificationId: string | null = null;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Request Interceptor
 * - Injects Authorization header with access token
 * - Adds device fingerprint header if available
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Let Axios/browser auto-set Content-Type for FormData (multipart/form-data with boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Add device fingerprint if stored in localStorage
    const deviceFingerprint = localStorage.getItem('corex-device-fingerprint');
    if (deviceFingerprint) {
      config.headers['X-Device-Fingerprint'] = deviceFingerprint;
    }

    // Add support session grant ID header if SuperAdmin is in a support session
    const { activeSession } = useSupportSessionStore.getState();
    if (activeSession) {
      config.headers['X-Support-Grant-Id'] = activeSession.grantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Auth endpoints that should NOT trigger token refresh on 401
 * These endpoints are expected to return 401 for invalid credentials
 */
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/mfa/verify',
  '/auth/refresh',
  '/password/reset',
  '/password/validate',
];

/**
 * Check if the URL is an auth endpoint
 */
const isAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

/** Longest plain-text error body we are willing to show the user verbatim. */
const MAX_RAW_ERROR_LENGTH = 300;

/**
 * Decide what to show when the error body is a bare string rather than our
 * JSON error shape.
 *
 * A short plain-text body is most likely a genuine message from the API and is
 * shown as-is. Anything HTML-shaped did NOT come from our API: an intermediary
 * replaced the response — antivirus TLS inspection, a corporate proxy, a
 * captive portal, or an nginx error page. Rendering that verbatim dumps an
 * entire HTML document into the UI, which is exactly what a Kaspersky
 * interception page did on the login screen on 2026-07-21.
 */
export const resolveStringErrorBody = (
  raw: string
): { message: string; code?: string } => {
  const body = raw.trim();
  const looksLikeHtml = /^(<!doctype|<html|<\?xml|<head|<body|<meta)/i.test(body);

  if (!looksLikeHtml && body.length <= MAX_RAW_ERROR_LENGTH) {
    return { message: body };
  }

  return {
    code: 'UNEXPECTED_RESPONSE',
    message:
      'The server returned an unexpected response. This is often caused by ' +
      'antivirus, a proxy, or a network portal intercepting the connection.',
  };
};

/**
 * Response Interceptor
 * - Handles 401 errors with token refresh
 * - Transforms error responses to consistent format
 */
apiClient.interceptors.response.use(
  (response) => {
    // Return the response data directly for successful requests
    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired
    // Skip token refresh logic for auth endpoints (they use 401 for invalid credentials)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint(originalRequest.url)) {
      const { refreshToken, logout } = useAuthStore.getState();

      // If no refresh token, logout and reject
      if (!refreshToken) {
        logout();
        sessionStorage.setItem('session_expired', 'Your session has expired. Please log in again.');
        window.location.href = '/auth/login';
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const response = await axios.post(
          `${projectConfig.api.baseUrl}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

        // Update store with new tokens
        const { user, login } = useAuthStore.getState();
        if (user) {
          login(user, newAccessToken, newRefreshToken);
        }

        // Process queued requests with new token
        processQueue(null, newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        processQueue(refreshError as Error, null);
        logout();
        sessionStorage.setItem('session_expired', 'Your session has expired. Please log in again.');
        window.location.href = '/auth/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden - Insufficient permissions
    if (error.response?.status === 403) {
      // Auto-exit support session if grant is invalid/expired
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = error.response.data as any;
      if (data?.error?.code === 'SUPPORT_GRANT_INVALID' || data?.error?.code === 'SUPPORT_READ_ONLY') {
        const { activeSession, exitSession } = useSupportSessionStore.getState();
        if (activeSession && data?.error?.code === 'SUPPORT_GRANT_INVALID') {
          exitSession();
        }
      }
      console.error('Access forbidden:', error.response.data);
    }

    // Handle network errors
    if (!error.response) {
      const networkError: ApiError = {
        code: 'NETWORK_ERROR',
        message: 'Unable to connect to server. Please check your internet connection.',
        status: 0,
      };

      // Show network error notification only once (skip if one is already visible).
      // While the browser is offline, stay silent: this is the expected state (already
      // surfaced by the offline banner), and offline-aware flows queue their work — a
      // "Connection Error" toast on every paused request would just be noise.
      const { notifications, addNotification } = useNotificationStore.getState();
      const alreadyShowing = networkErrorNotificationId &&
        notifications.some((n) => n.id === networkErrorNotificationId);

      if (!alreadyShowing && navigator.onLine) {
        networkErrorNotificationId = addNotification({
          type: 'error',
          title: 'Connection Error',
          message: networkError.message,
        });
      }

      return Promise.reject(networkError);
    }

    // (see resolveStringErrorBody below for the plain-string case)
    // Extract error message from various Spring Boot response formats
    // Use 'any' to handle different backend response formats flexibly
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = error.response.data as any;
    let errorMessage = 'An unexpected error occurred';
    let errorCode = 'UNKNOWN_ERROR';
    let errorDetails: Record<string, string[]> | undefined;
    let fieldErrors: Record<string, string> | undefined;
    let traceId: string | undefined;

    if (responseData) {
      // Format 1: New ErrorResponse format (constraint violations)
      // { "success": false, "status": 409, "errorCode": "DUPLICATE_ENTRY", "message": "...", "fieldErrors": {...} }
      if (responseData.errorCode) {
        errorCode = responseData.errorCode;
        errorMessage = responseData.message || errorMessage;
        fieldErrors = responseData.fieldErrors;
        traceId = responseData.traceId;
        // Convert fieldErrors to details for backwards compatibility
        if (fieldErrors) {
          errorDetails = Object.fromEntries(
            Object.entries(fieldErrors).map(([k, v]) => [k, [v]])
          );
        }
      }
      // Format 2: { "error": { "code": "...", "message": "..." } }
      else if (responseData.error?.message) {
        errorMessage = responseData.error.message;
        errorCode = responseData.error.code || errorCode;
        errorDetails = responseData.error.details;
      }
      // Format 3: { "message": "..." } (simple format)
      else if (responseData.message && typeof responseData.message === 'string') {
        errorMessage = responseData.message;
        errorCode = responseData.code || responseData.error || errorCode;
      }
      // Format 4: { "error": "...", "message": "..." } (Spring default)
      else if (responseData.error && typeof responseData.error === 'string') {
        errorMessage = responseData.message || responseData.error;
        errorCode = responseData.status?.toString() || errorCode;
      }
      // Format 5: { "errors": [...] } (validation errors)
      else if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        errorMessage = responseData.errors[0].message || responseData.errors[0];
        errorCode = 'VALIDATION_ERROR';
      }
      // Format 6: Direct string response.
      // Only trust a short, plain-text body. Anything HTML-shaped did not come
      // from our API — it is an intermediary (antivirus TLS inspection, a
      // corporate proxy, a captive portal, or an nginx error page) that
      // replaced the response. Showing that raw dumps a whole HTML document
      // into the UI, which is what a Kaspersky interception page did on
      // 2026-07-21.
      else if (typeof responseData === 'string' && responseData.length > 0) {
        const resolved = resolveStringErrorBody(responseData);
        errorMessage = resolved.message;
        if (resolved.code) errorCode = resolved.code;
      }
    }

    // Transform error response to consistent format
    const apiError: ApiError = {
      code: errorCode,
      message: errorMessage,
      status: error.response.status,
      fieldErrors,
      details: errorDetails,
      traceId,
    };

    // Note: We don't auto-show notifications here to avoid repeated toasts
    // from React Query retries and data fetching. Components should use
    // the useNotification hook in mutation onError callbacks to show toasts.
    //
    // Exceptions that are shown automatically:
    // - 409 Constraint violations: actionable user errors
    // - 429 Rate limit exceeded: important feedback for user to wait
    if (!isAuthEndpoint(originalRequest.url)) {
      if (error.response.status === 409) {
        const { addNotification } = useNotificationStore.getState();
        addNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage,
          fieldErrors,
        });
      } else if (error.response.status === 429) {
        const { addNotification } = useNotificationStore.getState();
        addNotification({
          type: 'warning',
          title: 'Too Many Requests',
          message: errorMessage || 'Please wait a moment and try again.',
        });
      }
    }

    return Promise.reject(apiError);
  }
);

export default apiClient;

/**
 * Helper function for GET requests
 */
export const get = async <T>(url: string, params?: Record<string, unknown>): Promise<T> => {
  const response = await apiClient.get<T>(url, { params });
  return response.data;
};

/**
 * Helper function for POST requests
 */
export const post = async <T>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
};

/**
 * Helper function for PUT requests
 */
export const put = async <T>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
};

/**
 * Helper function for PATCH requests
 */
export const patch = async <T>(url: string, data?: unknown): Promise<T> => {
  const response = await apiClient.patch<T>(url, data);
  return response.data;
};

/**
 * Helper function for DELETE requests
 */
export const del = async <T>(url: string): Promise<T> => {
  const response = await apiClient.delete<T>(url);
  return response.data;
};
